import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import PaginatedPage from '../../components/PaginatedPage'
import TableView from '../../components/TableView'
import UsersUpsertForm from './UsersUpsertForm'
import UserPresenter from '../../api/userPresenter'
import BasicDateRenderer from '../../utils/renderers'
import { Button, Modal, Input, Row, useWindowContext, Footer, noop } from '@iqueue/ui-kit'
import { v4 as uuid } from 'uuid'
import PermissionsTable from '../../components/PermissionsTable'
import PickNode from '../../components/picker/PickNode'
import PickRole from '../../components/picker/PickRole'
import NodeUserPresenter from '../../api/nodeUserPresenter'
import usePermissions from '../../components/usePermissions'

import './translations'
import useTranslate, { t } from '../../hooks/useTranslate'

function PermissionBox({ node, role, onEdit, onDelete, hasPermission }) {
  return <div className="permission-box">
    <span>{ node?.name } => { role?.name }</span>
    { hasPermission && <div>
      <Button secondary icon="edit" onClick={ onEdit }/>
      <Button danger icon="delete" onClick={ onDelete }/>
    </div> }
  </div>
}

export function UsersTable({ ...props }) {
  const [state, setState] = useState({})
  const [selectedRolePermission, setSelectedRolePermission] = useState()
  const [nodes, setNodes] = useState([])
  const [selectUser, setSelectUser] = useState({})
  const [selectedNode, setSelectedNode] = useState(undefined)
  const [opened, setOpened] = useState(false)
  const [openPermission, setOpenPermission] = useState(false)
  const cache = useRef()
  const { permissions } = usePermissions()
  const locale = useTranslate()

  const handleClickCreate = useCallback((e, v) => {
    e.stopPropagation()
    setState(v)
    setOpened(true)
  }, [])

  const hasPermission = useCallback(() => {
    return permissions.user?.includes('READ') && permissions.user?.includes('WRITE') && permissions.role?.includes('read') && permissions.sensor?.includes('read')
  }, [permissions])

  const handleClick = useCallback((nodeId, item) => {
    const node = cache.current?.items.find((n) => {
      return n.nodeId === nodeId
    })

    if (node) {
      setSelectedNode({ ...node, item })
      setOpened(true)
    } else {
      console.error('Node not found')
    }
  }, [cache])

  const handleDelete = useCallback(async (item) => {
    await NodeUserPresenter.delete(`${ item?.nodeId }/${ item?.userId }`)
    setNodes(prevNodes => prevNodes.filter(node => node.id !== item.id))
  }, [])

  const handleGetNodes = useCallback((e, item) => {
    e.stopPropagation()
    setNodes(item.nodes)
    setSelectUser(item)
    setOpenPermission(true)
  }, [])

  const schema = useMemo(() => [{
    key: 'username',
    title: t('user.table.username'),
    render: (v) => {
      return <span>{ v }</span>
    },
  }, {
    key: 'createdAt',
    title: t('user.table.createdAt'),
    render: BasicDateRenderer,
    width: '20rem',
  }, {
    key: 'addPermission',
    title: '',
    width: '8.4rem',
    style: {
      position: 'sticky',
      right: 0,
    },
    render: (_, item) => {
      return <>
        { hasPermission && <Button
          icon={ 'add' }
          primary
          onClick={ (e) => handleClickCreate(e, item) }/>
        }
        { hasPermission && <Button
          icon={ 'groups' }
          secondary
          onClick={ (e) => handleGetNodes(e, item) }/>
        }
        <div className={ 'nodes-counter' }>
          <span>{ item.nodes?.length || 0 }</span>
        </div>
      </>
    },
  }], [t, permissions])

  useEffect(() => {
    NodeUserPresenter.readIndexed(cache).catch(er => console.error(er))
  }, [])

  return <>
    <TableView
      permissions={ permissions[UserPresenter.key.toUpperCase()] }
      form={ UsersUpsertForm }
      entity={ 'users' }
      schema={ schema }
      minWidth={ '60rem' }
      { ...props }
    />

    <Modal
      fullscreen={ true }
      title={ selectedNode ? t('user.title.updateAccess') : t('user.title.createAccess') }
      onApply={ async (item) => {

        const res = {
          ...item,
          userId: selectedNode?.item?.id || state.id,
        }

        Object.keys(res).forEach(key => {
          if (key.startsWith('max')) {
            delete res[key]
          }
        })

        if (selectedNode) {
          await NodeUserPresenter.upsert(res)
        } else {
          await NodeUserPresenter.post('', res)
        }

        props.reload()

      } }
      isOpened={ opened }
      onClose={ () => {
        setSelectedNode({})
        setSelectedRolePermission(null)
        setOpened(false)
      } }
      footerActions={ [
        { key: 'cancel', title: t('user.form.cancel') },
        { key: 'apply', title: t('user.form.apply'), submit: true, primary: true },
      ] }
    >

      <Row>
        <Input
          size={ 12 }
          name={ 'id' }
          hidden
          value={ selectedNode?.node?.id || uuid() }
        />
      </Row>

      <Row>
        <Input
          size={ 12 }
          name={ 'userId' }
          disabled
          placeholder={ t('user.form.username') }
          value={ selectedNode?.item?.username || state.username }
        />
      </Row>

      <Row>
        <PickRole
          required
          size={ 12 }
          name={ 'roleId' }
          current={ state }
          placeholder={ t('user.form.role') }
          value={ selectedNode?.role?.id }
          onChange={ (_, b) => {
            setSelectedRolePermission(b.permissions)
          } }
        />
      </Row>

      <Row>
        <PickNode
          required
          size={ 12 }
          name={ 'nodeId' }
          current={ state }
          placeholder={ t('user.form.node') }
          value={ selectedNode?.node?.id }
        />
      </Row>

      <Row>
        <PermissionsTable
          data={ selectedNode || {} }
          user={ state }
          permissions={ selectedRolePermission }
        />
      </Row>
    </Modal>

    <Modal
      isOpened={ openPermission }
      onClose={ () => {
        setOpenPermission(false)
        setNodes([])
        props.reload()
      } }
      fullscreen
      title={ `${ t('user.title.accessesForUser') } ${ selectUser?.username }` }
    >

      { nodes.map((v) => {
        return <PermissionBox
          key={ v.id }
          node={ v.node }
          role={ v.role }
          onEdit={ () => handleClick(v.nodeId, selectUser) }
          onDelete={ () => handleDelete(v) }
          hasPermission={ hasPermission }
        />
      }) }

    </Modal>
  </>
}

export default function UsersPage() {
  const { setTitle, togglePaddings, toggleScrollbars } = useWindowContext()
  const locale = useTranslate()
  const translatedTitle = useMemo(() => t('menu.users'), [locale])

  useEffect(() => {
    setTitle(translatedTitle)
    togglePaddings(false)
    toggleScrollbars(true)
  }, [translatedTitle])

  return <PaginatedPage
    presenter={ UserPresenter }
    component={ UsersTable }
  />
}
